import { useEffect, useState } from 'preact/hooks';
import type { PublishingDetails, RuntimeSnapshot } from '../app/runtime-types.ts';
import type { CatalogHoverCaptureState, CatalogHoverVideoArtifact } from '../publishing/index.ts';
import type { CoasterProject } from '../schema/index.ts';
import { CloseButton } from './primitives.tsx';
import { LoadingMark } from './LoadingMark.tsx';
import { UI_COPY } from './ui-copy.ts';

export type PublishPanelStatus =
  | 'local_only'
  | 'draft_private'
  | 'unlisted_read_only'
  | 'pending_review'
  | 'public'
  | 'rejected'
  | 'hidden'
  | 'archived';

interface PublishPanelProps {
  readonly open: boolean;
  readonly sessionId: number;
  readonly snapshot: RuntimeSnapshot;
  readonly project: CoasterProject | null;
  readonly cloudAvailable: boolean;
  readonly cloudVersion: number | null;
  readonly status: PublishPanelStatus;
  readonly statusUpdatedAt: string | null;
  readonly busy: boolean;
  readonly submitting: boolean;
  readonly message: string | null;
  readonly conflictVersion: number | null;
  readonly heroPreviewUrl: string | null;
  readonly heroCaptureState: 'none' | 'captured' | 'stale';
  readonly catalogHoverPreviewUrl: string | null;
  readonly catalogHoverState: CatalogHoverCaptureState;
  readonly catalogHoverProgress: number;
  readonly catalogHoverArtifact: CatalogHoverVideoArtifact | null;
  readonly publicUrl: string | null;
  readonly sourceAttribution?: string | null;
  readonly onClose: () => void;
  readonly onAutoFrame: () => void;
  readonly onCaptureHero: () => Promise<void>;
  readonly onSubmit: (details: PublishingDetails) => Promise<void>;
  readonly onRetryCatalogHover: () => Promise<void>;
  readonly onLoadCloudConflict: () => Promise<void>;
  readonly onKeepLocalConflict: () => Promise<void>;
  readonly onRefreshStatus: () => Promise<void>;
}

const SUGGESTED_TAGS = [
  ['family_friendly', 'Family Friendly'],
  ['scenic', 'Scenic'],
  ['thrilling', 'Thrilling'],
  ['intense', 'Intense'],
  ['inversions', 'Inversions'],
  ['launch', 'Launch'],
  ['lift_hill', 'Lift Hill'],
  ['smooth', 'Smooth'],
  ['compact', 'Compact'],
  ['long_ride', 'Long Ride'],
] as const;

const SUGGESTED_TAG_IDS = new Set<string>(SUGGESTED_TAGS.map(([id]) => id));

function formatStatusTimestamp(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function statusLabel(status: PublishPanelStatus): string {
  switch (status) {
    case 'draft_private':
      return 'Private draft';
    case 'unlisted_read_only':
      return 'Read-only link active';
    case 'pending_review':
      return 'Waiting for Review by Buncha Games';
    case 'public':
      return 'Public';
    case 'rejected':
      return 'Changes requested';
    case 'hidden':
      return 'Hidden by moderation';
    case 'archived':
      return 'Archived';
    default:
      return 'Saved on this device';
  }
}

export function PublishPanel(props: PublishPanelProps) {
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [retainedTags, setRetainedTags] = useState<string[]>([]);
  const [allowRemix, setAllowRemix] = useState(true);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [seededSessionId, setSeededSessionId] = useState(-1);

  useEffect(() => {
    if (!props.open || !props.project) return;
    setDescription(props.project.metadata.description);
    setTags(props.project.metadata.tags.filter((tag) => SUGGESTED_TAG_IDS.has(tag)));
    setRetainedTags(props.project.metadata.tags.filter((tag) => !SUGGESTED_TAG_IDS.has(tag)));
    setAllowRemix(props.project.permissions.allowRemix);
    setSeededSessionId(props.sessionId);
  }, [props.open, props.project?.projectId, props.sessionId]);

  if (!props.open) return null;

  if (!props.project || seededSessionId !== props.sessionId)
    return (
      <section
        class="publish-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-panel-loading-title"
        data-testid="publish-panel"
      >
        <header>
          <div>
            <LoadingMark compact label="Loading your coaster" />
            <h2 id="publish-panel-loading-title">Loading your coaster…</h2>
          </div>
          <CloseButton label="Close publish panel" onClick={props.onClose} />
        </header>
      </section>
    );

  const details: PublishingDetails = {
    description,
    tags: [...retainedTags, ...tags].slice(0, 10),
    allowReadOnlyShare: props.project.permissions.allowReadOnlyShare,
    allowRemix,
  };
  const technicalReady = props.snapshot.fullValidation.technicalEligible;
  const needsHero = props.heroPreviewUrl === null;
  const heroStateLabel = props.heroPreviewUrl
    ? props.status === 'public'
      ? 'Public Preview'
      : props.status === 'pending_review'
        ? 'Preview uploaded'
        : 'Saved on this device'
    : props.heroCaptureState === 'stale'
      ? 'Needs recapture'
      : 'Needed';
  const canSubmit =
    props.cloudAvailable &&
    technicalReady &&
    !needsHero &&
    !props.busy &&
    props.status !== 'pending_review' &&
    props.status !== 'hidden' &&
    props.status !== 'archived';
  const submitDisabledReason = !props.cloudAvailable
    ? 'Sign in on Buncha Games to publish.'
    : !technicalReady
      ? 'Fix the problems in Ride Check first.'
      : needsHero
        ? 'Capture a Preview Image first.'
        : props.status === 'pending_review'
          ? 'This revision is already waiting for review.'
          : props.status === 'hidden' || props.status === 'archived'
            ? 'This coaster cannot accept a new revision right now.'
            : props.busy
              ? 'Please wait for the current action to finish.'
              : null;
  const statusTimestamp = formatStatusTimestamp(props.statusUpdatedAt);
  const statusOnly = props.submitting || props.status === 'pending_review';
  const copyUrl = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyMessage('Public link copied.');
    } catch {
      setCopyMessage('Select and copy the public link above.');
    }
  };
  return (
    <section
      class="publish-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="publish-panel-title"
      data-testid="publish-panel"
    >
      <header>
        <div>
          <h2 id="publish-panel-title">{UI_COPY.publish.title}</h2>
        </div>
        <CloseButton label="Close publish panel" onClick={props.onClose} />
      </header>

      <div class="publish-scroll">
        <div class={`publish-state publish-state-${props.status}`} role="status">
          <strong>Buncha Games Coaster Submission Status</strong>
          <span>{statusLabel(props.status)}</span>
          {statusTimestamp && (
            <small>
              {props.status === 'pending_review' ? 'Submitted' : 'Updated'} {statusTimestamp}
            </small>
          )}
          {props.cloudVersion !== null && (
            <button
              type="button"
              class="publish-refresh"
              disabled={props.busy}
              onClick={() => void props.onRefreshStatus()}
            >
              Refresh status
            </button>
          )}
        </div>

        {props.submitting && (
          <div class="publish-submitting" role="status" aria-live="polite">
            <LoadingMark label="Submitting coaster" />
            <strong>Submitting your coaster…</strong>
            <span>{props.message ?? 'Preparing your immutable revision for Buncha Games.'}</span>
          </div>
        )}

        {!statusOnly && (
          <>
            {props.sourceAttribution && (
              <p class="publish-lineage">
                Remixed from {props.sourceAttribution}. Your draft is independent.
              </p>
            )}

            {props.publicUrl && (
              <section class="publish-section public-destination" aria-label="Public coaster">
                <div>
                  <span class="eyebrow">Public destination</span>
                  <strong>Your approved coaster is live.</strong>
                </div>
                <label>
                  <span>Public coaster link</span>
                  <input value={props.publicUrl} readOnly aria-label="Public coaster link" />
                </label>
                <div class="publish-inline-actions">
                  <a class="primary-action" href={props.publicUrl} target="_blank" rel="noreferrer">
                    View public coaster
                  </a>
                  <button type="button" onClick={props.onClose}>
                    Edit draft
                  </button>
                  <button type="button" onClick={() => void copyUrl(props.publicUrl!)}>
                    Copy link
                  </button>
                </div>
              </section>
            )}

            <section class="publish-section" aria-labelledby="publish-check-title">
              <div class="publish-section-heading">
                <div>
                  <span>1</span>
                  <h3 id="publish-check-title">Ride Check</h3>
                </div>
                <strong class={technicalReady ? 'is-ready' : 'is-blocked'}>
                  {props.snapshot.fullValidation.pending
                    ? 'Checking…'
                    : technicalReady
                      ? 'Ready'
                      : 'Fix blockers'}
                </strong>
              </div>
              <div class="publish-metrics">
                <span>
                  Intensity
                  <strong>{props.snapshot.fullValidation.intensity?.label ?? 'Not checked'}</strong>
                </span>
                <span>
                  Top speed
                  <strong>
                    {props.snapshot.fullValidation.topSpeedMph === null
                      ? '—'
                      : `${props.snapshot.fullValidation.topSpeedMph.toFixed(1)} mph`}
                  </strong>
                </span>
                <span>
                  Problems
                  <strong>
                    {
                      props.snapshot.fullValidation.issues.filter(
                        (issue) => issue.category === 'technical' && issue.severity === 'blocker',
                      ).length
                    }
                  </strong>
                </span>
              </div>
              {props.snapshot.fullValidation.intensity &&
                ['intense', 'extreme'].includes(props.snapshot.fullValidation.intensity.level) && (
                  <p class="publish-intensity-warning">
                    Riders will see a prominent {props.snapshot.fullValidation.intensity.label}{' '}
                    intensity warning.
                  </p>
                )}
            </section>

            <section class="publish-section" aria-labelledby="publish-details-title">
              <div class="publish-section-heading">
                <div>
                  <span>2</span>
                  <h3 id="publish-details-title">Details</h3>
                </div>
              </div>
              <label>
                <span>Description</span>
                <textarea
                  value={description}
                  maxLength={500}
                  rows={3}
                  placeholder="What should riders know before they board?"
                  onInput={(event) => setDescription(event.currentTarget.value)}
                />
                <small>{description.length}/500</small>
              </label>
              <fieldset class="publish-tag-picker">
                <legend>Tags</legend>
                <div role="group" aria-label="Suggested tags">
                  {SUGGESTED_TAGS.map(([id, label]) => {
                    const selected = tags.includes(id);
                    const selectionLimit = Math.max(0, 10 - retainedTags.length);
                    return (
                      <button
                        key={id}
                        type="button"
                        aria-pressed={selected}
                        disabled={!selected && tags.length >= selectionLimit}
                        onClick={() =>
                          setTags((current) =>
                            selected ? current.filter((tag) => tag !== id) : [...current, id],
                          )
                        }
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <small>Choose up to {Math.max(0, 10 - retainedTags.length)}.</small>
              </fieldset>
            </section>

            <section class="publish-section" aria-labelledby="publish-hero-title">
              <div class="publish-section-heading">
                <div>
                  <span>3</span>
                  <h3 id="publish-hero-title">{UI_COPY.publish.previewTitle}</h3>
                </div>
                <strong class={needsHero ? 'is-blocked' : 'is-ready'}>{heroStateLabel}</strong>
              </div>
              <div class="hero-capture-preview">
                {props.heroPreviewUrl ? (
                  <img src={props.heroPreviewUrl} alt="Coaster Preview Image" />
                ) : (
                  <div>
                    <span class="eyebrow">16:9 preview</span>
                    <strong>Show players your coaster</strong>
                  </div>
                )}
              </div>
              <p class="publish-preview-description">{UI_COPY.publish.previewDescription}</p>
              <div class="publish-inline-actions">
                <button type="button" onClick={props.onAutoFrame}>
                  {props.heroPreviewUrl
                    ? UI_COPY.publish.autoFrameAgain
                    : UI_COPY.publish.autoFrame}
                </button>
                <button
                  type="button"
                  class="primary-action"
                  disabled={props.busy}
                  onClick={() => void props.onCaptureHero()}
                >
                  {props.busy
                    ? 'Capturing…'
                    : props.heroPreviewUrl
                      ? UI_COPY.publish.replacePreview
                      : UI_COPY.publish.capturePreview}
                </button>
              </div>
              <div class="catalog-hover-preview">
                <div class="catalog-hover-heading">
                  <div>
                    <strong>8-Second Motion Preview</strong>
                    <span>Muted · 16:9 · loops while players browse</span>
                  </div>
                  <strong class={`catalog-hover-status state-${props.catalogHoverState}`}>
                    {props.catalogHoverState === 'waiting_for_preview'
                      ? 'Waiting for Preview Image'
                      : props.catalogHoverState === 'generating'
                        ? `Generating ${Math.round(props.catalogHoverProgress * 100)}%`
                        : props.catalogHoverState === 'ready'
                          ? 'Ready'
                          : props.catalogHoverState === 'unavailable'
                            ? 'Unavailable'
                            : props.catalogHoverState === 'stale'
                              ? 'Out of date'
                              : 'Failed'}
                  </strong>
                </div>
                {props.catalogHoverPreviewUrl ? (
                  <video
                    src={props.catalogHoverPreviewUrl}
                    poster={props.heroPreviewUrl ?? undefined}
                    controls
                    loop
                    muted
                    playsInline
                    autoPlay={!props.snapshot.settings.reducedMotion}
                    preload="metadata"
                    aria-label="Buncha Games motion preview"
                  />
                ) : (
                  <div class="catalog-hover-placeholder">
                    <span>
                      {props.catalogHoverState === 'waiting_for_preview'
                        ? 'Capture the Preview Image to start the motion preview.'
                        : props.catalogHoverState === 'generating'
                          ? 'Orbiting the full coaster diorama…'
                          : 'The Preview Image remains available as the Buncha Games fallback.'}
                    </span>
                  </div>
                )}
                {props.catalogHoverArtifact && (
                  <small>
                    {props.catalogHoverArtifact.width}×{props.catalogHoverArtifact.height} ·{' '}
                    {(props.catalogHoverArtifact.byteSize / 1_000_000).toFixed(2)} MB · muted
                  </small>
                )}
                {(props.catalogHoverState === 'failed' || props.catalogHoverState === 'stale') && (
                  <button
                    type="button"
                    class="secondary-action"
                    disabled={props.busy || !props.heroPreviewUrl}
                    onClick={() => void props.onRetryCatalogHover()}
                  >
                    Try Motion Preview Again
                  </button>
                )}
              </div>
            </section>

            <section class="publish-section" aria-labelledby="publish-access-title">
              <div class="publish-section-heading">
                <div>
                  <span>4</span>
                  <h3 id="publish-access-title">Publishing Settings</h3>
                </div>
              </div>
              <label class="publish-toggle">
                <input
                  type="checkbox"
                  checked={allowRemix}
                  onChange={(event) => setAllowRemix(event.currentTarget.checked)}
                />
                <span>
                  <strong>Allow remixes</strong>
                  <small>
                    A remix starts as an independent private draft and cannot change your coaster.
                  </small>
                </span>
              </label>
            </section>
          </>
        )}

        {!props.submitting && (props.message || copyMessage) && (
          <p class="publish-message" role="status">
            {copyMessage ?? props.message}
          </p>
        )}
        {props.conflictVersion !== null && (
          <div class="publish-conflict" role="alert" data-testid="cloud-conflict">
            <strong>This draft changed in another session.</strong>
            <span>Choose which copy to keep. Nothing will be overwritten automatically.</span>
            <div>
              <button
                type="button"
                disabled={props.busy}
                onClick={() => void props.onLoadCloudConflict()}
              >
                Load cloud copy
              </button>
              <button
                type="button"
                disabled={props.busy}
                onClick={() => void props.onKeepLocalConflict()}
              >
                Keep this copy
              </button>
            </div>
          </div>
        )}
      </div>

      {!statusOnly && (
        <footer>
          {submitDisabledReason &&
            (needsHero ? (
              <button
                id="publish-submit-reason"
                class="publish-footer-guidance"
                type="button"
                onClick={() =>
                  document.getElementById('publish-hero-title')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  })
                }
              >
                Next: {submitDisabledReason}
              </button>
            ) : (
              <small id="publish-submit-reason">{submitDisabledReason}</small>
            ))}
          <button
            type="button"
            class="primary-action"
            disabled={!canSubmit}
            title={submitDisabledReason ?? undefined}
            aria-describedby={submitDisabledReason ? 'publish-submit-reason' : undefined}
            onClick={() => void props.onSubmit(details)}
            data-testid="submit-for-review"
          >
            {props.publicUrl ? 'Submit Buncha Games Update' : 'Submit to Buncha Games'}
          </button>
        </footer>
      )}
    </section>
  );
}
